import React, { useState } from 'react';
import { Button, Modal, Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import GLOBALVAR from '../../constants';

const ESignButton = ({ formData, isCompact, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [showInterestModal, setShowInterestModal] = useState(false);
    const [interestRate, setInterestRate] = useState('5');
    const [error, setError] = useState(null);
    
    const [loanType, setLoanType] = useState('new');
    const [category, setCategory] = useState('personal');
    const [hasCollateral, setHasCollateral] = useState('no');

    const handleESign = () => {
        setError(null);
        setShowInterestModal(true);
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            
            // Format the request according to the backend expectations
            const response = await axios.post(`${GLOBALVAR['SERVER_ROOT']}/api/signwell/send`, {
                formData: {
                    // Personal Information
                    firstName: formData.firstName,
                    middleName: formData.middleName,
                    lastName: formData.lastName,
                    
                    // Address
                    houseStreet: formData.houseStreet,
                    barangay: formData.barangay,
                    municipality: formData.municipality,
                    province: formData.province,
                    zipCode: formData.zipCode,
                    
                    // Contact
                    mobileNumber1: formData.mobileNumber1,
                    email: formData.email,
                    
                    // Employment
                    employer: formData.employer,
                    monthlyIncome: formData.monthlyIncome,
                    sourceOfIncome: formData.sourceOfIncome,
                    
                    // Loan Details
                    loanAmount: formData.loanAmount,
                    loanPurpose: formData.loanPurpose,
                    loanTerm: formData.loanTerm,
                    
                    // Spouse Information
                    spouseName: formData.spouseName,
                    spouseEmployer: formData.spouseEmployer,
                    
                    // Co-maker Information
                    coMakerFirstName: formData.coMakerFirstName,
                    coMakerMiddleName: formData.coMakerMiddleName,
                    coMakerLastName: formData.coMakerLastName,
                    coMakerEmail: formData.coMakerEmail,
                    coMakerHouseStreet: formData.coMakerHouseStreet,
                    coMakerBarangay: formData.coMakerBarangay,
                    coMakerMunicipality: formData.coMakerMunicipality,
                    coMakerProvince: formData.coMakerProvince,
                    coMakerZipCode: formData.coMakerZipCode,
                    
                    // New fields
                    loanType,
                    category,
                    hasCollateral
                },
                interestRate: `${interestRate}%`,
                document: {
                    template: 'HAYAHAY_LOAN_AGREEMENT_AND_PROMISSORY_NOTE.pdf',
                    name: 'Loan Agreement and Promissory Note',
                    signers: [
                        {
                            email: formData.email,
                            full_name: `${formData.firstName} ${formData.middleName || ''} ${formData.lastName}`,
                            role: 'Borrower',
                            signing_order: 1
                        },
                        {
                            email: formData.coMakerEmail,
                            full_name: `${formData.coMakerFirstName} ${formData.coMakerMiddleName || ''} ${formData.coMakerLastName}`,
                            role: 'Co-Maker',
                            signing_order: 2
                        }
                    ],
                    fields: [
                        {
                            type: 'signature',
                            role: 'Borrower',
                            page: 2, // Adjust based on your template
                            x: 100,
                            y: 500,
                            width: 300,
                            height: 47,
                            required: true,
                            label: 'Borrower Signature'
                        },
                        {
                            type: 'signature',
                            role: 'Co-Maker',
                            page: 2, // Adjust based on your template
                            x: 415,
                            y: 500,
                            width: 300,
                            height: 47,
                            required: true,
                            label: 'Co-Maker Signature'
                        },
                        {
                            type: 'signature',
                            role: 'Borrower',
                            page: 4, // Adjust based on your template
                            x: 125,
                            y: 950,
                            width: 300,
                            height: 47,
                            required: true,
                            label: 'Borrower Signature'
                        },
                        {
                            type: 'signature',
                            role: 'Co-Maker',
                            page: 4, // Adjust based on your template
                            x: 420,
                            y: 950,
                            width: 300,
                            height: 47,
                            required: true,
                            label: 'Co-Maker Signature'
                        },
                        {
                            type: 'signature',
                            role: 'Borrower',
                            page: 5, // Adjust based on your template
                            x: 280,
                            y: 630,
                            width: 300,
                            height: 47,
                            required: true,
                            label: 'Borrower Signature'
                        }
                    ],
                    expires_in: 7,
                    message: `Please review and sign the Loan Agreement and Promissory Note for the amount of ₱${parseFloat(formData.loanAmount).toLocaleString()}`
                }
            });

            if (response.data.success) {
                setShowInterestModal(false);
                if (onSuccess) {
                    onSuccess('Document sent successfully! Signers will receive an email shortly.');
                }
            } else {
                throw new Error(response.data.message || 'Failed to send document');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to process and send document');
            console.error('Document Processing Error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button 
                variant="primary"
                onClick={handleESign}
                disabled={loading}
                size="sm"
            >
                {loading ? (
                    <>
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                        />
                        Processing...
                    </>
                ) : (
                    'Send E-Sign'
                )}
            </Button>

            <Modal
                show={showInterestModal}
                onHide={() => !loading && setShowInterestModal(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton={!loading}>
                    <Modal.Title>Loan Details Confirmation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Loan Type</Form.Label>
                        <div>
                            <Form.Check
                                inline
                                type="radio"
                                id="loan-type-new"
                                name="loanType"
                                label="New"
                                value="new"
                                checked={loanType === 'new'}
                                onChange={(e) => setLoanType(e.target.value)}
                                disabled={loading}
                            />
                            <Form.Check
                                inline
                                type="radio"
                                id="loan-type-renewal"
                                name="loanType"
                                label="Renewal"
                                value="renewal"
                                checked={loanType === 'renewal'}
                                onChange={(e) => setLoanType(e.target.value)}
                                disabled={loading}
                            />
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Category</Form.Label>
                        <div>
                            <Form.Check
                                inline
                                type="radio"
                                id="category-personal"
                                name="category"
                                label="Personal"
                                value="personal"
                                checked={category === 'personal'}
                                onChange={(e) => setCategory(e.target.value)}
                                disabled={loading}
                            />
                            <Form.Check
                                inline
                                type="radio"
                                id="category-business"
                                name="category"
                                label="Business"
                                value="business"
                                checked={category === 'business'}
                                onChange={(e) => setCategory(e.target.value)}
                                disabled={loading}
                            />
                            <Form.Check
                                inline
                                type="radio"
                                id="category-other"
                                name="category"
                                label="Other"
                                value="other"
                                checked={category === 'other'}
                                onChange={(e) => setCategory(e.target.value)}
                                disabled={loading}
                            />
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Has Collateral</Form.Label>
                        <div>
                            <Form.Check
                                inline
                                type="radio"
                                id="collateral-yes"
                                name="hasCollateral"
                                label="Yes"
                                value="yes"
                                checked={hasCollateral === 'yes'}
                                onChange={(e) => setHasCollateral(e.target.value)}
                                disabled={loading}
                            />
                            <Form.Check
                                inline
                                type="radio"
                                id="collateral-no"
                                name="hasCollateral"
                                label="No"
                                value="no"
                                checked={hasCollateral === 'no'}
                                onChange={(e) => setHasCollateral(e.target.value)}
                                disabled={loading}
                            />
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Interest Rate (%)</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.01"
                            value={interestRate}
                            onChange={(e) => setInterestRate(e.target.value)}
                            disabled={loading}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowInterestModal(false)}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? 'Processing...' : 'Proceed'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {error && (
                <div className="text-danger mt-2 small">
                    {error}
                </div>
            )}
        </>
    );
};

export default ESignButton;